import { useState, useRef } from "react";
import SEOHead from "../components/SEOHead";
import { motion } from "framer-motion";
import { 
  MessageSquare, 
  Send, 
  Loader2, 
  CheckCircle,
  User,
  Mail,
  FileText,
  Phone,
  Clock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { sanitizeName } from "../lib/inputSanitizers";
import { recaptchaHeaders } from "../lib/recaptcha";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useScrollIntoViewWhenReady } from "../hooks/useScrollIntoViewWhenReady";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SupportPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  
  const [formData, setFormData] = useState({
    guest_name: "",
    guest_email: "",
    subject: "",
    message: "",
    related_booking_id: "",
  });

  const supportFormRef = useRef(null);
  useScrollIntoViewWhenReady(supportFormRef, !submitted, { mobileOnly: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.guest_name || !formData.guest_email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      const rcHeaders = await recaptchaHeaders("support");
      const res = await axios.post(`${API}/support/tickets`, {
        ...formData,
        guest_email: formData.guest_email.toLowerCase(),
      }, { headers: rcHeaders });
      
      setTicketNumber(res.data.ticket_number);
      setSubmitted(true);
      toast.success("Support ticket created successfully!");
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast.error("Failed to create support ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container-empire max-w-xl" data-testid="support-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <span className="font-cookie text-3xl text-empire-gold block mb-2">
            We've got your message
          </span>
          <h1 className="text-3xl font-bold text-white mb-4">
            Support Ticket Created
          </h1>
          <p className="text-neutral-400 mb-6">
            Your ticket number is:
          </p>
          <div className="glass rounded-xl p-4 mb-8">
            <span className="text-2xl font-mono font-bold text-empire-gold">
              {ticketNumber}
            </span>
          </div>
          <p className="text-neutral-400 text-sm mb-8">
            We'll respond to your inquiry at <span className="text-white">{formData.guest_email}</span> as soon as possible.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                guest_name: "",
                guest_email: "",
                subject: "",
                message: "",
                related_booking_id: "",
              });
            }}
            className="btn-empire-outline"
          >
            Submit Another Request
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container-empire max-w-2xl pb-6" data-testid="support-page">
      <SEOHead page="support" />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6 md:mb-8"
      >
        <span className="font-cookie text-2xl md:text-3xl text-empire-gold block mb-1">
          We're here to help
        </span>
        <h1 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">
          Contact Support
        </h1>
        <p className="text-neutral-400 text-sm md:text-base">
          Have a question or need assistance? Send us a message and we'll get back to you.
        </p>
      </motion.div>

      {/* Contact Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass rounded-xl p-4 md:p-6 mb-6 md:mb-8"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-4">
          {/* Email */}
          <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:text-center">
            <div className="w-10 h-10 rounded-lg bg-empire-gold/10 border border-empire-gold/30 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-empire-gold" />
            </div>
            <div className="sm:mt-2">
              <h3 className="text-sm font-semibold text-white">Email</h3>
              <a 
                href="mailto:support@empiredancekenya.com" 
                className="text-empire-gold text-xs sm:text-sm hover:underline"
              >
                support@empiredancekenya.com
              </a>
            </div>
          </div>

          {/* Phone */}
          <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:text-center">
            <div className="w-10 h-10 rounded-lg bg-empire-gold/10 border border-empire-gold/30 flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-empire-gold" />
            </div>
            <div className="sm:mt-2">
              <h3 className="text-sm font-semibold text-white">Phone</h3>
              <a 
                href="tel:+254715353535" 
                className="text-empire-gold text-sm hover:underline whitespace-nowrap"
              >
                +254 715 353 535
              </a>
            </div>
          </div>

          {/* Hours */}
          <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:text-center">
            <div className="w-10 h-10 rounded-lg bg-empire-gold/10 border border-empire-gold/30 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-empire-gold" />
            </div>
            <div className="sm:mt-2">
              <h3 className="text-sm font-semibold text-white mb-1">Hours</h3>
              <div className="text-xs sm:text-sm text-neutral-400">
                <p>Mon–Fri: 6 AM – 10 PM</p>
                <p>Sat–Sun: 8 AM – 8 PM</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Support Form */}
      <motion.div
        ref={supportFormRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="scroll-mt-24 md:scroll-mt-0"
        data-testid="support-form-section"
      >
        <Card className="card-empire">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-base md:text-lg">
              <MessageSquare className="w-5 h-5 text-empire-gold" />
              Send us a message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-white text-sm">Your Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.guest_name}
                      onChange={(e) => setFormData({ ...formData, guest_name: sanitizeName(e.target.value) })}
                      className="input-empire pl-10 h-11"
                      required
                      data-testid="input-name"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-white text-sm">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.guest_email}
                      onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                      className="input-empire pl-10 h-11"
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="booking" className="text-white text-sm">Booking Reference (Optional)</Label>
                <Input
                  id="booking"
                  type="text"
                  placeholder="EDK-20250101-ABC123"
                  value={formData.related_booking_id}
                  onChange={(e) => setFormData({ ...formData, related_booking_id: e.target.value.toUpperCase() })}
                  className="input-empire font-mono h-11 text-sm"
                  data-testid="input-booking"
                />
                <p className="text-xs text-neutral-500">
                  If your inquiry is related to a booking, enter the reference number
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="subject" className="text-white text-sm">Subject *</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    id="subject"
                    type="text"
                    placeholder="How can we help?"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="input-empire pl-10 h-11"
                    required
                    data-testid="input-subject"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-white text-sm">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Tell us more about your inquiry..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="input-empire min-h-[120px] md:min-h-[150px] resize-none text-sm"
                  required
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full btn-empire h-11 md:h-12 text-sm md:text-base"
                data-testid="submit-support-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
